import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '@/components/common/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import EmailFilters from '@/components/emails/EmailFilters';
import EmailList from '@/components/emails/EmailList';
import EmailDetail from '@/components/emails/EmailDetail';
import FilterCreationDialog from '@/components/rules/FilterCreationDialog';
import { useEmails, useUpdateEmail, useDeleteEmail } from '@/hooks/useEmails';
import { emailsAPI } from '@/utils/api';
import api from '@/utils/api';

export default function Emails() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    // Initialize filters from URL query parameters
    const seen = searchParams.get('seen');
    const actionNeeded = searchParams.get('action_needed');
    const orderBy = searchParams.get('order_by');
    const orderDir = searchParams.get('order_dir');

    return {
      search: '',
      account_id: '',
      category: '',
      priority: '',
      starred: false,
      archived: false,
      seen: seen === 'false' ? false : seen === 'true' ? true : undefined,
      action_needed: actionNeeded === 'true' ? true : undefined,
      order_by: orderBy || 'date',
      order_dir: orderDir || 'DESC',
      limit: 50,
      offset: 0,
    };
  });
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const { data: emailsData, isLoading } = useEmails(filters);
  const updateMutation = useUpdateEmail();
  const deleteMutation = useDeleteEmail();

  const emails = emailsData?.emails || emailsData || [];

  // Clear URL query parameters after applying them to filters
  useEffect(() => {
    const hasFilterParams = searchParams.has('seen') ||
                           searchParams.has('action_needed') ||
                           searchParams.has('order_by') ||
                           searchParams.has('order_dir');

    if (hasFilterParams && !searchParams.has('id')) {
      // Clear query params without navigation
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle email ID from URL query parameter
  useEffect(() => {
    const emailId = searchParams.get('id');
    if (!emailId) return;

    // Try to find email in already loaded list first
    const emailInList = emails.find(e => e.id === emailId);
    if (emailInList) {
      setSelectedEmail(emailInList);
      setSearchParams({});
      return;
    }

    // If not in list, fetch it by ID
    if (!isLoading) {
      api.get(`/emails/${emailId}`)
        .then(response => {
          if (response.data.success) {
            setSelectedEmail(response.data);
            setSearchParams({});
          }
        })
        .catch(err => {
          console.error('Failed to load email from URL:', err);
          setSearchParams({});
        });
    }
  }, [searchParams, emails, isLoading, setSearchParams]);
  const pagination = emailsData?.pagination;

  const handleFilterChange = (newFilters) => {
    setFilters({ ...newFilters, offset: 0 });
    setSelectedEmail(null);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      account_id: '',
      category: '',
      priority: '',
      starred: false,
      archived: false,
      limit: 50,
      offset: 0,
    });
    setSelectedEmail(null);
  };

  const handleStar = (email) => {
    updateMutation.mutate({
      id: email.id,
      data: { starred: !email.starred },
    });
  };

  const handleArchive = (email) => {
    updateMutation.mutate({
      id: email.id,
      data: { archived: !email.archived },
    });
  };

  const handleDelete = (email) => {
    deleteMutation.mutate(email.id);
    if (selectedEmail?.id === email.id) {
      setSelectedEmail(null);
    }
  };

  const handlePrevPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  const handleNextPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const handleSelectEmail = (emailId, selected) => {
    setSelectedEmailIds(prev =>
      selected
        ? [...prev, emailId]
        : prev.filter(id => id !== emailId)
    );
  };

  const handleSelectAndMarkRead = (email) => {
    setSelectedEmail(email);
    if (!email.seen) {
      updateMutation.mutate({
        id: email.id,
        data: { seen: true },
      });
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      setSelectedEmailIds(emails.map(e => e.id));
    } else {
      setSelectedEmailIds([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedEmailIds([]);
  };

  const handleCreateFilter = async () => {
    if (selectedEmailIds.length === 0) return;

    setIsAnalyzing(true);
    try {
      const response = await emailsAPI.analyzeBulk(selectedEmailIds);
      setAnalysisData(response.data);
      setShowFilterDialog(true);
    } catch (error) {
      console.error('Failed to analyze emails:', error);
      alert('Failed to analyze emails. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Emails</h1>
        <p className="hidden sm:block text-sm text-muted-foreground mt-1">
          View and manage your emails
        </p>
      </div>

      <EmailFilters
        filters={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Email List - Hidden on mobile when email is selected */}
        <div className={`space-y-4 ${selectedEmail ? 'hidden lg:block' : ''}`}>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {pagination?.total ? `${pagination.total} emails` : `${emails.length} emails`}
            </p>

            {pagination && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={filters.offset === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {filters.offset + 1}-{Math.min(filters.offset + filters.limit, pagination.total)} of {pagination.total}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {selectedEmailIds.length > 0 && (
                <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {selectedEmailIds.length} email{selectedEmailIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={handleClearSelection}
                        variant="ghost"
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCreateFilter}
                        disabled={isAnalyzing}
                      >
                        {isAnalyzing ? 'Analyzing...' : 'Create Filter'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <EmailList
                emails={emails}
                selectedId={selectedEmail?.id}
                onSelect={handleSelectAndMarkRead}
                onStar={handleStar}
                onArchive={handleArchive}
                onDelete={handleDelete}
                selectedIds={selectedEmailIds}
                onSelectEmail={handleSelectEmail}
                onSelectAll={handleSelectAll}
              />
            </>
          )}
        </div>

        {/* Email Detail - Full screen on mobile, sticky sidebar on desktop */}
        <div className={`
          ${selectedEmail ? 'fixed inset-0 z-50 lg:relative lg:z-auto' : 'hidden'}
          lg:sticky lg:top-6 lg:h-[calc(100vh-12rem)]
          border rounded-lg bg-card
        `}>
          <EmailDetail
            email={selectedEmail}
            onClose={() => setSelectedEmail(null)}
            onStar={handleStar}
            onArchive={handleArchive}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {/* Filter Creation Dialog */}
      <FilterCreationDialog
        analysisData={analysisData}
        isOpen={showFilterDialog}
        onClose={() => {
          setShowFilterDialog(false);
          setAnalysisData(null);
          setSelectedEmailIds([]);
        }}
      />
    </div>
  );
}
